import { intervalToDuration, isBefore } from 'date-fns';
import { either, option } from 'fp-ts';
import i18n from 'i18next';
import { pipe } from 'fp-ts/function';
import { parseDate } from 'utils/datetime';
import { createDateFormatConfig, createDateTimeFormat } from './formats';
import { DateFormat } from './types';
import { determineDurationField } from './utils';

type DateRangeValues = 'duration' | 'range';

export const localizeDateRange = (
  a: Date,
  b: Date = parseDate(undefined, true),
  locale?: string,
): Record<DateRangeValues, string | undefined> => {
  // Ensure start is on or before the end
  const [start, end] = isBefore(a, b) ? [a, b] : [b, a];

  const formatter = createDateTimeFormat(locale, createDateFormatConfig(DateFormat.Short));

  const range = formatter.formatRange(start, end);
  const duration = pipe(
    { start, end },
    (r) => either.tryCatch(() => intervalToDuration(r), either.left), // convert to a duration and catch errors
    either.map(determineDurationField), // get largest unit of measurement
    either.map(([count, key]) => i18n.t(`common:datetime.units.${key}`, { count })), // translate
    option.fromEither, // hide the error
    option.toUndefined, // return undefined if there was an error
  );

  return { duration, range };
};
